
import { useEffect, useRef, useState } from 'react';
import DataTable from '../../../components/data-table/DataTable';
import SearchTable from '../../../components/search-table/SearchTable';
import { StyledWorks } from './works.styled';
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from '../../../helpers/helpers';
import { useSelector, useDispatch } from 'react-redux';
import { AccountStructure } from '../../../models/accounts-structure';
import { RootState } from '../../../redux/store';
import { Skeleton } from '@mui/material';
import { CanceledError } from 'axios';
import ListItemIcon from '@mui/material/ListItemIcon';
import EditIcon from '@mui/icons-material/Edit';
import { setWorksSearch } from '../../../redux/features/browse';
import { getWorks } from '../../../services/worksService';
import { Work, WorkSearch } from '../../../models/work';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from "@emotion/react";
import { useErrorBoundary } from "react-error-boundary";
import { handleApiErrors } from '../../../services/errorHandling';
import { editLastBread, getLastAvailableLink } from '../../../components/seperator/Seperator.utils';
import { useGetAccountStructureQuery } from '../../../redux/api/accounts-structure.api';
import { DocumentWorkAdd } from '../../../models/document-work';


interface SearchForm {
    [key: string]: string; // Index signature allowing any string key with string values
}
interface WorksProps {
    searchFormData?: SearchForm;
}

function Works({ searchFormData }: WorksProps) {

    const { showBoundary } = useErrorBoundary();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const theme = useTheme();

    const { data: worksStructure } = useGetAccountStructureQuery("עבודה");
    
    const worksSearch = useSelector((state: RootState) => state.browse.worksSearch)

    const [data, setData] = useState<Work[]>([]);
    const [searchStructure, setSearchStructure] = useState<AccountStructure[]>();
    const [tableStructure, setTableStructure] = useState<AccountStructure[]>();
    const [sortKey, setSortKey] = useState<string>('id');
    const [sortMethod, setSortMethod] = useState<string>('DESC');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const options = [
        {
            label: "עריכה",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><EditIcon fontSize="small" /></ListItemIcon>,
            href: ""
        },
    ];

    const workSearchForm = useForm<WorkSearch>({defaultValues:{
        ...worksSearch,
        ...searchFormData
    }});

    useEffect(() => {
        setSearchStructure(worksStructure?.filter(field => field.show_search))
        setTableStructure(worksStructure?.filter(field => field.show_browse))
    }, [worksStructure]);


    useEffect(() => {
        if (sortKey && sortMethod) {
            workSearchForm.handleSubmit(onSearch)();
        }
    }, [sortKey, sortMethod]);

    useEffect(() => {
        workSearchForm.watch(() => {
            workSearchForm.handleSubmit(onSearch)();
        })
    }, [workSearchForm.watch]);

    function handleSortKeyChanged(value: string) {
        setSortKey(value);
    }

    function handleSortMethodChanged(value: string) {
        setSortMethod(value);
    }

    const onSearch: SubmitHandler<WorkSearch> = (data) => {
        dispatch(setWorksSearch(data));
        getData(data);
    }

    async function getData(data: WorkSearch) {

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        setIsLoading(true);

        try {
            const responseData = await getWorks(sortKey || 'id', "10", sortMethod || 'ASC', ["customData"], deleteEmptyFieldsFromObject(data), abortControllerRef.current.signal);
            setData(responseData || []);
            setIsLoading(false);
        } catch (error: unknown) {
            if (error instanceof CanceledError) {
                return;
            }
            handleApiErrors(error, showBoundary);
        }

    }


    function rowChosen(row: Work) {
        
        if (location.state?.breadcrumbData) {

            const updatedWorks: DocumentWorkAdd[] = [...location.state.works];
            updatedWorks[location.state.workRowIndex] = {
                ...row,
                work_id: row.id,
                hours: 1,
                discount: 0,
                price: row.price ?? 0,
                net: row.price ?? 0,
                total: row.price ?? 0
            };

            navigate(getLastAvailableLink(location.state.breadcrumbData), {
                replace: true,
                state: {
                    ...location.state,
                    // chosenItem: row
                    works: updatedWorks,
                    breadcrumbData: location.state.breadcrumbData.slice(0, -1)
                }
            });
        }
    }

    function createState(){
        return {
            ...location.state,
        }
    }

    function createBreadcrumbData(state: any){

        const breadcrumbData = [
            {
                name: "כרטסות",
            },
            {
                name: "קודי עבודה",
                link: "/works",
            },
        ];

        return [...(editLastBread(state?.breadcrumbData, `/works`, state) ?? breadcrumbData)];
    }

    function editSearchStructure(){

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/works/structure/search', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה חיפוש" }]
            }
        });
    }

    function editBrowseStructure(){

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/works/structure/browse', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה דפדוף" }]
            }
        });
    }

    function reset() {

        const values = setAllValuesToNull(workSearchForm.getValues());

        for (const key in values) {
            if (values.hasOwnProperty(key)) {
                workSearchForm.setValue(key as keyof WorkSearch, values[key as keyof WorkSearch]);
            }
        }
    }
    
    return (
        <StyledWorks>
            {searchStructure ?
                <SearchTable structure={searchStructure} setStructure={editSearchStructure} reset={reset} useForm={workSearchForm} style={{ backgroundColor: "rgb(35, 48, 68)", padding: 5, marginButton: 20 }} /> :
                <Skeleton variant="rectangular" height={100} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
            {tableStructure ?
                <DataTable structure={tableStructure} setStructure={editBrowseStructure} sortKey={sortKey} setSortKey={handleSortKeyChanged} sortMethod={sortMethod} setSortMethod={handleSortMethodChanged} data={data} isLoading={isLoading} options={options} selectRow={location.state?.from ? true : false} onSelectRow={rowChosen}/> :
                <Skeleton variant="rectangular" height={150} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
        </StyledWorks>
    );
}

export default Works;